import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { isNotificationReplyRoute } from '../utils/tenant-route.util';
import { ERROR_MESSAGES } from '../../shared/constants/messages.constants';
import { AuthService } from '../services/auth.service';
import { SITE_LINKS } from '../../shared/constants/site-links.constants';
import { StorageService } from '../services/storage.service';
import { STORAGE } from '../../shared/constants/storage.constants';
import { ActionFeedbackService } from '../services/action-feedback.service';
import { API_ENDPOINTS } from '../../shared/constants/api.constants';

/** Quick-reply page handles 403/500 inline — suppress global toasts on those routes. */
function isNotificationReplyEndpoint(url: string): boolean {
  return (
    url.includes(API_ENDPOINTS.NOTIFICATIONS.REPLY_CONTEXT) ||
    url.includes(API_ENDPOINTS.NOTIFICATIONS.REPLY_SUBMIT)
  );
}

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const storageService = inject(StorageService);
  const actionFeedback = inject(ActionFeedbackService);
  const router = inject(Router);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Other errors are not caught (404 etc) and should be handled by the callers if necessary
      if (error.status === 401) {
        const url = error.url || '';
        const onReplyPage = isNotificationReplyRoute(router.url);
        if (!isNotificationReplyEndpoint(url) && !onReplyPage) {
          actionFeedback.showError(ERROR_MESSAGES.SESSION_EXPIRED);
          authService.logout();
          storageService.remove(STORAGE.USER);
          storageService.remove(STORAGE.ACCESS_TOKEN);
          storageService.remove(STORAGE.USER_PERMISSIONS);//remove additional storage items as needed
          sessionStorage.clear();

          // Reload the app
          window.location.href = SITE_LINKS.AUTH.LOGIN;
        }
      }
      else if (error.status === 500) {
        // Check if the operation actually succeeded despite 500 status
        // Some APIs return 500 but the response body indicates success
        const responseBody = error.error;
        const url = error.url || '';
        
        // For delete operations, be more lenient - if the response has any success indicator, suppress the toast
        const isDeleteOperation = url.includes('/delete') || url.includes('/bulk-delete');
        
        // Job cost reversal: not a success — the line drawer re-reads the line and reports the outcome itself,
        // so the global toast stays out of the way instead of doubling (or contradicting) it.
        const isJobCostReverse = url.includes('/job-cost/entries/reverse');

        if (responseBody?.success === true || 
            responseBody?.message?.toLowerCase().includes('success') ||
            (isDeleteOperation && (responseBody?.data !== undefined || responseBody?.message))) {
          // Operation succeeded, don't show error toast
        } else if (!isNotificationReplyEndpoint(url) && !isJobCostReverse) {
          // Actual server error, show toast
          actionFeedback.showError(ERROR_MESSAGES.INTERNAL_SERVER_ERROR);
        }
      }
      else if (error.status === 403) {
        // Contacts endpoints handle 403 errors with floating toolbar, so no toast needed
        const url = error.url || '';
        const isNotificationReply = isNotificationReplyEndpoint(url);
        const isContactsEndpoint = url.includes('/contacts/paginated') || 
                                  url.includes('/contacts/delete') || 
                                  url.includes('/contacts/bulk-delete') ||
                                  (url.includes('/contacts') && !url.includes('/contacts/add') && !url.includes('/contacts/edit'));
        
        // Production list / project shell show inline access message; avoid duplicate toast
        const isProductionListOrProjectLoad =
          url.includes('/api/shard/production/projects/paginated') ||
          url.includes('/api/shard/production/projects/by-id');
        // The Start Production preview only decides whether "View Job Costing" shows; no access just hides it
        const isStartProductionPreview = url.includes('/api/shard/projects/start-production/preview');

        // Only show toast for non-contacts 403 errors
        // Contacts 403 errors are handled by components using floating toolbar
        if (!isContactsEndpoint && !isProductionListOrProjectLoad && !isNotificationReply && !isStartProductionPreview) {
          actionFeedback.showError(ERROR_MESSAGES.ACCESS_DENIED_SECTION);
        }
      }

      return throwError(() => error);
    })
  );
};
